import { useLocation, useParams } from "react-router-dom";

/** Scope segment in the URL (first path segment). 'nation' is the nation scope segment. */
export type UrlScope = "nation" | "church" | "district";

/** Scope segment used in API routes (e.g. /nation/:id/, /church/:id/, /district/:id/). */
export type ApiScope = "nation" | "church" | "district";

const URL_SCOPES: readonly UrlScope[] = ["nation", "church", "district"];

/**
 * Maps URL scope to the scope segment used in API routes.
 * Use this when building API paths from the current URL scope.
 *
 * @example
 * // URL: /nation/1/tags  → mapUrlScopeToApiScope('nation') === 'nation'
 * // API: GET /nation/1/reporttag/query
 * const { scope, scopeId } = useScope();
 * const apiScope = mapUrlScopeToApiScope(scope);
 * const path = `/${apiScope}/${scopeId}/reporttag/query`;
 */
export function mapUrlScopeToApiScope(urlScope: UrlScope): ApiScope {
    return urlScope;
}

export interface ScopeResult {
    scope: UrlScope;
    scopeId: number;
}

/**
 * Returns the scope and scope id from the current route when the URL follows
 * the pattern /{scope}/{scopeId}/... (e.g. /nation/1/tags, /church/123/dashboard).
 * The first segment is the scope; the value immediately after it is the scope id.
 * Use mapUrlScopeToApiScope(scope) when building API routes.
 *
 * @returns { scope, scopeId } or undefined if the path doesn't match or id is invalid.
 *
 * @example
 * const result = useScope();
 * if (result) {
 *   const apiScope = mapUrlScopeToApiScope(result.scope);
 *   const path = `/${apiScope}/${result.scopeId}/reporttag/query`;
 * }
 */
export function useScope(): ScopeResult | undefined {
    const { pathname } = useLocation();
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length < 2) return undefined;
    const [scope, scopeIdStr] = segments;
    if (!URL_SCOPES.includes(scope as UrlScope)) return undefined;
    const scopeId = Number(scopeIdStr);
    if (Number.isNaN(scopeId)) return undefined;
    return { scope: scope as UrlScope, scopeId };
}

/**
 * Returns only the scope id from the current route (convenience wrapper around useScope()).
 * Use useScope() when you need both scope and scopeId, e.g. when calling APIs with mapUrlScopeToApiScope().
 */
export function useScopeId(): number | undefined {
    return useScope()?.scopeId;
}

/**
 * Returns a numeric route param by name.
 *
 * Use when you need a specific param (e.g. departmentId). For the current
 * context’s scope id (the id right after /nation/, /church/, or /district/),
 * prefer useScopeId() so you don’t depend on param names.
 *
 * @param paramName - The route param key (e.g. 'churchId', 'nationId', 'districtId').
 * @returns The param value as a number, or undefined if missing/invalid.
 *
 * @example
 * // Route: /church/:churchId/reports/view
 * const churchId = useRouteParamId('churchId');
 */
export function useRouteParamId(paramName: string): number | undefined {
    const params = useParams<Record<string, string>>();
    const value = params[paramName];
    if (value === undefined || value === "") return undefined;
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
}
